import React, { useState, useEffect } from 'react';
import { BetConfig } from '../api/betConfigsApi';
import { getTeams, Team } from '../api/teamsApi';
import { getMatches, Match } from '../api/matchesApi';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface Props {
  config: BetConfig;
  onSave: (answer: Record<string, any>) => Promise<void>;
  onCancel: () => void;
}

const CorrectAnswerEditor: React.FC<Props> = ({ config, onSave, onCancel }) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [value, setValue] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const dataType = config.view_schema?.data;

  // Seed current value from existing correct_answer
  useEffect(() => {
    const ca = config.correct_answer;
    if (!ca) return;
    if (dataType === 'teams' || dataType === 'ranked_teams' || dataType === 'match') {
      setValue(String((ca as any).id ?? ''));
    } else {
      setValue(String((ca as any).value ?? ''));
    }
  }, [config.correct_answer, dataType]);

  useEffect(() => {
    if (dataType === 'teams' || dataType === 'ranked_teams') {
      getTeams().then(setTeams).catch(console.error);
    } else if (dataType === 'match') {
      getMatches().then(setMatches).catch(console.error);
    }
  }, [dataType]);

  const buildAnswer = (): Record<string, any> | null => {
    if (!value.trim()) return null;
    if (dataType === 'teams' || dataType === 'ranked_teams') {
      const id = parseInt(value, 10);
      if (isNaN(id)) return null;
      return { id };
    }
    if (dataType === 'match') {
      return { id: value };
    }
    if (dataType === 'number') {
      const num = parseInt(value, 10);
      if (isNaN(num)) return null;
      return { value: num };
    }
    // players / default → string value
    return { value: value.trim() };
  };

  const handleSave = async () => {
    const answer = buildAnswer();
    if (!answer) {
      alert('Please enter a valid value.');
      return;
    }
    setSaving(true);
    try {
      await onSave(answer);
    } finally {
      setSaving(false);
    }
  };

  const renderInput = () => {
    if (dataType === 'teams' || dataType === 'ranked_teams') {
      return (
        <select
          value={value}
          onChange={e => setValue(e.target.value)}
          className="bg-gray-800 border border-gray-600 text-white text-sm rounded px-2 py-1 flex-1 focus:outline-none focus:border-blue-500"
        >
          <option value="">Select team…</option>
          {teams.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      );
    }

    if (dataType === 'match') {
      return (
        <select
          value={value}
          onChange={e => setValue(e.target.value)}
          className="bg-gray-800 border border-gray-600 text-white text-sm rounded px-2 py-1 flex-1 focus:outline-none focus:border-blue-500"
        >
          <option value="">Select match…</option>
          {matches.map(m => {
            const home = m.home_team?.name ?? m.home_team_placeholder;
            const away = m.away_team?.name ?? m.away_team_placeholder;
            return (
              <option key={m.id} value={m.id}>{home} vs {away}</option>
            );
          })}
        </select>
      );
    }

    if (dataType === 'number') {
      return (
        <input
          type="number"
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder="e.g. 5"
          className="bg-gray-800 border border-gray-600 text-white text-sm rounded px-2 py-1 w-24 focus:outline-none focus:border-blue-500"
        />
      );
    }

    // players or unknown → plain text
    return (
      <input
        type="text"
        value={value}
        onChange={e => setValue(e.target.value)}
        placeholder="e.g. Lionel Messi"
        className="bg-gray-800 border border-gray-600 text-white text-sm rounded px-2 py-1 flex-1 focus:outline-none focus:border-blue-500"
      />
    );
  };

  return (
    <div className="flex items-center gap-1 min-w-[220px]">
      {renderInput()}
      <button
        onClick={handleSave}
        disabled={saving}
        className="text-green-400 hover:text-green-300 p-1 disabled:opacity-50"
        title="Save"
      >
        <CheckIcon className="h-4 w-4" />
      </button>
      <button
        onClick={onCancel}
        disabled={saving}
        className="text-gray-400 hover:text-gray-300 p-1"
        title="Cancel"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

export default CorrectAnswerEditor;
